import { cn } from "@/lib/utils";
import type { IssueState } from "@/lib/api/issues";

/**
 * Where an issue stands — SDD Appendix A's seven, and what each one MEANS.
 *
 * The labels are doing work. `verified` and `closed` are two different facts —
 * the platform proved the fix, and a person decided the matter is over — and
 * the console this replaced had one word, "resolved", for both. Under ARC-039
 * only the Validation Engine can reach `verified`, so collapsing them would let
 * a human decision read as a machine proof.
 */
const META: Record<IssueState, { label: string; soft: string; fg: string; title: string }> = {
  open: {
    label: "Open", soft: "bg-sev-high-soft", fg: "text-sev-high",
    title: "Promoted from a finding and not yet triaged.",
  },
  triaged: {
    label: "Triaged", soft: "bg-sev-medium-soft", fg: "text-sev-medium",
    title: "Assessed by an analyst, not yet being fixed.",
  },
  in_fix: {
    label: "In fix", soft: "bg-primary/10", fg: "text-primary",
    title: "Being fixed. It leaves this state by being submitted for validation.",
  },
  validating: {
    label: "Validating", soft: "bg-primary/10", fg: "text-primary",
    // SDD §7.2, FR-57: `error`/`inconclusive` is a SELF-LOOP. A result does not
    // always move the issue — a target that was down, or a session that could
    // not be re-established, leaves it here, flagged for manual review.
    title: "Being re-tested; can't be moved until results are in.",
  },
  verified: {
    label: "Verified", soft: "bg-ok-soft", fg: "text-ok",
    title: "The platform re-tested and could not reproduce it. Only the Validation Engine reaches this.",
  },
  reopened: {
    label: "Reopened", soft: "bg-sev-critical-soft", fg: "text-sev-critical",
    // SDD §7.2 has TWO edges into `reopened`: validating → reopened when a
    // rule fails, and closed → reopened when a new finding matches the
    // issue's grouping key.
    title: "Either validation reproduced it, so the fix did not hold, or it was closed and a new finding matched it again.",
  },
  closed: {
    label: "Closed", soft: "bg-muted", fg: "text-muted-foreground",
    title: "A person closed a verified fix.",
  },
};

export function IssueStateBadge({
  state,
  unrecognised,
}: {
  state: IssueState;
  /** The raw string, when the platform named a state this build does not know. */
  unrecognised?: string;
}) {
  if (unrecognised) {
    return (
      <span
        title="This console does not recognise the state the platform reported."
        className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 font-mono text-xs font-medium text-muted-foreground"
      >
        {unrecognised}
      </span>
    );
  }
  const m = META[state];
  return (
    <span
      title={m.title}
      className={cn("inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium", m.soft, m.fg)}
    >
      {m.label}
    </span>
  );
}

/** The console's name for a state — the same word its badge shows. */
export function issueStateLabel(state: IssueState): string {
  return META[state].label;
}

/** The verb for a transition, as an action rather than a state name. */
export const TRANSITION_LABEL: Record<"triaged" | "in_fix" | "closed", string> = {
  triaged: "Mark triaged",
  in_fix: "Move to in fix",
  closed: "Close",
};
